import {isHandledCustomerReferenceError} from "@/lib/customer-reference-error";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {
  createActivity,
  fetchActivities,
  fetchActivityStats,
  cancelActivity,
  updateActivityDate,
  type CreateActivityInput,
  type ActivityType,
  type Activity,
} from "@/lib/api/activities";
import {toast} from "@sakura-grp/ui/sonner";

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  phone: "電話",
  message: "メッセージ",
  visit: "訪問",
  letter: "DM",
};

/**
 * 営業活動一覧を取得するフック
 */
export function useActivities(params: {
  customer_id?: string;
  hostess_id?: string;
  store_id?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["activities", params],
    queryFn: () => fetchActivities(params),
    enabled: !!(params.customer_id || params.hostess_id),
  });
}

/**
 * 営業統計を取得するフック
 */
export function useActivityStats(params: {
  customer_id: string;
  hostess_id: string;
  days?: number;
}) {
  return useQuery({
    queryKey: ["activity-stats", params],
    queryFn: () => fetchActivityStats(params),
    enabled: !!(params.customer_id && params.hostess_id),
  });
}

/**
 * 営業活動を記録するフック（即座に保存）
 */
export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createActivity,
    onSuccess: (_data, variables) => {
      const typeLabel = ACTIVITY_TYPE_LABELS[variables.activity_type];
      toast.success(`${typeLabel}を記録しました`, {
        duration: 2000,
      });

      // キャッシュを無効化して最新データを取得
      queryClient.invalidateQueries({queryKey: ["activities"]});
      queryClient.invalidateQueries({queryKey: ["activity-stats"]});
      // 顧客詳細の活動データ（カウント・ステータス含む）を更新
      queryClient.invalidateQueries({queryKey: ["customer-activities", variables.customer_id]});
      // ダッシュボードの顧客カード（活動カウント・営業ステータス）を更新
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      // 営業履歴ページの一覧を更新
      queryClient.invalidateQueries({queryKey: ["announcements"]});
    },
    onError: (error: Error, variables) => {
      if (isHandledCustomerReferenceError(error)) return;
      const typeLabel = ACTIVITY_TYPE_LABELS[variables.activity_type];
      toast.error(`${typeLabel}の記録に失敗しました`, {
        description: error.message,
        duration: 4000,
      });
    },
  });
}

/**
 * 営業活動の活動日時を訂正するフック（変更履歴は DB トリガーで自動記録）
 */
export function useUpdateActivityDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({id, activityDate}: {id: string; activityDate: string}) =>
      updateActivityDate(id, activityDate),
    onSuccess: () => {
      toast.success("営業報告時刻を訂正しました", {
        duration: 2000,
      });

      queryClient.invalidateQueries({queryKey: ["activities"]});
      queryClient.invalidateQueries({queryKey: ["activity-stats"]});
      queryClient.invalidateQueries({queryKey: ["customer-activities"]});
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      queryClient.invalidateQueries({queryKey: ["announcements"]});
    },
    onError: (error: Error) => {
      if (isHandledCustomerReferenceError(error)) return;
      toast.error("時刻の訂正に失敗しました", {
        description: error.message,
        duration: 4000,
      });
    },
  });
}

/**
 * 営業活動をキャンセルするフック（イミュータブル: キャンセルのみ許可）
 */
export function useCancelActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      hostessId,
      cancelReason,
    }: {
      id: string;
      hostessId: string;
      cancelReason?: string;
    }) => cancelActivity(id, hostessId, cancelReason),
    onSuccess: () => {
      toast.success("営業活動をキャンセルしました", {
        duration: 2000,
      });

      queryClient.invalidateQueries({queryKey: ["activities"]});
      queryClient.invalidateQueries({queryKey: ["activity-stats"]});
      // 顧客詳細の活動データを更新（prefix invalidate: 全顧客分）
      queryClient.invalidateQueries({queryKey: ["customer-activities"]});
      // ダッシュボードの顧客カードを更新
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      // 営業履歴ページの一覧を更新
      queryClient.invalidateQueries({queryKey: ["announcements"]});
    },
    onError: (error: Error) => {
      if (isHandledCustomerReferenceError(error)) return;
      toast.error("キャンセルに失敗しました", {
        description: error.message,
        duration: 4000,
      });
    },
  });
}

export type {CreateActivityInput, Activity, ActivityType};
